import * as React from "react";
import _ from 'lodash';
import * as WidgetStructs from "../WidgetStructs";

import { StateManager } from "@Utils/StateManager";
import { TitleAlign, ITsViewFormat, IWidgetPadding } from '@ModelViews/ModelViews';
import { __LAST_UPDATE_TS_KEY__ } from './Utils/LastUpdateTs';

// Material UI components
import { List, ListItem } from 'material-ui/List';
import Toggle from 'material-ui/Toggle';
import FlatButton from 'material-ui/FlatButton';
import Divider from 'material-ui/Divider';
import IconMenu from 'material-ui/IconMenu';
import IconButton from 'material-ui/IconButton';
import { grey500 } from 'material-ui/styles/colors';
import MenuItem from 'material-ui/MenuItem';

// Icons
import InputIcon from 'material-ui/svg-icons/file/cloud-download';
import OutputIcon from 'material-ui/svg-icons/file/cloud-upload';
import DefaultEditIcon from 'material-ui/svg-icons/editor/mode-edit';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';
import OpenInDialogIcon from 'material-ui/svg-icons/action/open-in-new';
import MacroIcon from 'material-ui/svg-icons/action/extension';
import NoDataIcon from 'material-ui/svg-icons/notification/priority-high';
import ErrorIcon from 'material-ui/svg-icons/alert/error';
import CopyIcon from 'material-ui/svg-icons/content/content-copy';
import ToolbarIcon from 'material-ui/svg-icons/action/tab';

import { isWebThingKey, PortData, ReduxThingState, StateStatus, WebThingKeys } from '@API/WebNodeTypes';
import type { WebThing, WebThingPort } from '@API/WebThing';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { WebNode } from '@API/WebNode';
import { Sandbox } from '@Utils/Sandbox/Sandbox';
import { isAnimationEnabled } from '@Utils/Globals';
import { DebugEx } from '@Utils/DebugEx';
import { Plegma } from '@API/Api';
import { CopyToClipboard } from '@Utils/Utils';
import { DateFormat } from '@Utils/Formats';
import type { IWidgetDataValidateResp } from '../IWidgetDataValidateResp';
import type { IWidgetDialog } from '../IWidgetClassParent';
import { Localization, LocalizationMap } from '@Primitive/Localization/Localization';
import type { DialogConf } from '@Primitive/ExDialog';
import { JSEditor } from '@Primitive/JSEditor';
import { MonacoEditor } from '@Primitive/Monaco';
import { eIconType, SettingsElementAll, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsListAsync';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { ToolbarIconInfo, ToolbarIconInfo_GetSettingList } from "@Primitive/ToolbarIconButton";
import { BoardTheming } from "@UI/Dashboard/BoardTheming";

// ================================================================================================
export interface IWidgetThings extends WidgetStructs.IWidget {
    NodeKey: string;
    ThingKey: string;
    WebModuleKey: string;
}
export type IWidgetThingsDictionary = { [key: string]: IWidgetThings };
// ------------------------------------------------------------------------------------------------
export interface IWidgetThingsProps<T> extends WidgetStructs.IWidgetProps<T> {
    PortsStates?: { [key: string]: PortData };
    PortVersion?: number;
    LocalizationMap?: LocalizationMap;
    SendPortEvent: (portKey: string, state: any) => void;

    // Link to web thing struct
    NodeKey: string;
    ThingKey: string;
}
// ------------------------------------------------------------------------------------------------
export interface WidgetDataTransform {
    PortState: any;
    ExtraInfo: any;
    WidgetData: any;
}
// ------------------------------------------------------------------------------------------------
export interface PartialIWidgetThingsProps<T> extends Partial<IWidgetThingsProps<T>> {
}
// ------------------------------------------------------------------------------------------------
export interface WidgetThingsSettingsState {
    GenericConfigurationsInit: WidgetStructs.WidgetThingGenericConfiguration;
    UnSavedSettings: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface WidgetThingsSettingsProps<T> {
    WidgetData: any;
    RenderWidgetSettings: React.ReactElement<any>;
    DescriptionJSXFunc: (width: number, height: number) => React.ReactNode;
    NodeKey: string;
    ThingKey: string;
    Thing: WebThing;
    WidgetDefaultValues: { [key: string]: any };
    ValidateValues: (key: string, value: any) => IWidgetDataValidateResp;
    Dialog: IWidgetDialog;
    GenericConfigurations: WidgetStructs.WidgetThingGenericConfiguration;
    onSaveSettings: (settings: WidgetStructs.WidgetThingGenericConfiguration) => void;
}
// ------------------------------------------------------------------------------------------------
export class WidgetThingsSettings<T> extends React.Component<WidgetThingsSettingsProps<T>, WidgetThingsSettingsState>{
    static whyDidYouRender = true;
    constructor(props) {
        super(props);
        this.state = {
            UnSavedSettings: false,
            GenericConfigurationsInit: _.cloneDeep(props.GenericConfigurations)
        }
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetThingsSettingsProps<T>) {
        if (!_.isEqual(this.props.GenericConfigurations, prevProps.GenericConfigurations))
            this.setState({ GenericConfigurationsInit: _.cloneDeep(this.props.GenericConfigurations), UnSavedSettings: false });
    }
    // ---------------------------------------------------------------------------------------------------
    showJSEditorDialog(port: WebThingPort, data: WidgetStructs.WidgetThingGenericConfiguration, updateData: (newData: WidgetStructs.WidgetThingGenericConfiguration) => void) {
        let jsCode = "";
        if (isDefined(data.Description.PortMacros) &&
            _.has(data.Description.PortMacros, port.PlegmaPort.PortKey) &&
            _.has(data.Description.PortMacros[port.PlegmaPort.PortKey], "onDataTransform"))
            jsCode = data.Description.PortMacros[port.PlegmaPort.PortKey]["onDataTransform"].Code;

        let inputData = "";
        let webNode = WebNode.GetInstanceKey(this.props.NodeKey);
        if (_.has(webNode.DBGMsgsRoot.Things, port.WebThing.ThingKey) &&
            _.has(webNode.DBGMsgsRoot.Things[port.WebThing.ThingKey].Ports, port.PortKey)
        ) {
            let dbgPort = webNode.DBGMsgsRoot.Things[port.WebThing.ThingKey].Ports[port.PortKey];
            if (dbgPort.States.length > 0) {
                if (isDefined(dbgPort.States[0].OrigState))
                    inputData = dbgPort.States[0].OrigState;
                else
                    inputData = dbgPort.States[0].State;
            }
        }

        const helpMarkdown = `
# onDataTransform

The called function is have one argument the \`input\` and is return the final port state that we are sending to Cyan.

The \`input\` have the following format :

\`\`\`js
{
    PortState: any;
    ExtraInfo: any;
    WidgetData: any;
    ReduxState: any;
}
\`\`\`

The initial port state is in \`input.PortState\`.
The function need to return the final port state.

Simple JS example:

\`\`\`js
console.log(input);

return input.PortState;
\`\`\`
`;

        var dialogInfo: DialogConf = {
            contentStaticSize: 0.95,
            bodyFunc: (w, h) => {
                let theme = BoardTheming.GetActiveTheme();
                return <JSEditor
                    width={w}
                    height={h}
                    toolbarHeight={theme.dialog.height}
                    Title="onDataTransform"
                    JSCode={jsCode}
                    InputData={inputData}
                    HelpMarkdown={helpMarkdown}
                    onCloseRequest={() => this.props.Dialog.close()}
                    onSaveRequest={(jsCode) => {
                        if (!isDefined(data.Description.PortMacros))
                            data.Description.PortMacros = {};
                        if (!_.has(data.Description.PortMacros, port.PlegmaPort.PortKey))
                            data.Description.PortMacros[port.PlegmaPort.PortKey] = {}
                        if (!_.has(data.Description.PortMacros[port.PlegmaPort.PortKey], "onDataTransform"))
                            data.Description.PortMacros[port.PlegmaPort.PortKey]["onDataTransform"] = {
                                Description: "Transform data",
                                Enable: true,
                                Name: "onDataTransform",
                                Code: jsCode,
                                IsValid: false // TODO: send validation status
                            };
                        else
                            data.Description.PortMacros[port.PlegmaPort.PortKey]["onDataTransform"].Code = jsCode
                        updateData(data);
                    }}

                    RunCode={(
                        code: string,
                        input: { [name: string]: any },
                        onResults: (results: any) => void,
                        onError?: (error: string) => void
                    ) => {
                        let states = StateManager.GetInstanceKey(this.props.NodeKey).GetStates();
                        return Sandbox.GetInstance("WidgetThingsJSOutputRunner").Eval(
                            code,
                            { PortState: input, ExtraInfo: null, ThingStates: states, WidgetData: this.props.WidgetData } as WidgetDataTransform,
                            (newState) => onResults(newState),
                            (error) => onError(error)
                        );
                    }}
                />
            }
        };
        this.props.Dialog.show(dialogInfo);
    }
    // ---------------------------------------------------------------------------------------------------
    showEditDefaultValueDialog(port: WebThingPort, data: WidgetStructs.WidgetThingGenericConfiguration, updateData: (newData: WidgetStructs.WidgetThingGenericConfiguration) => void) {
        var widgetDefaultValues = this.props.WidgetDefaultValues;
        var vKey = port.PlegmaPort.PortKey;
        var defaultValue = (_.has(data.Description.DefaultValues, vKey)) ?
            data.Description.DefaultValues[vKey] : // Old default value
            (_.has(widgetDefaultValues, vKey)) ? widgetDefaultValues[vKey] : // User defined
                "";
        if (port.PlegmaPort.Type == Plegma.ePortType.JsonString)
            defaultValue = JSON.stringify(defaultValue, null, 4);
        var dialogInfo: DialogConf = {
            title: "Default Value",
            contentStaticSize: 0.8,
            actions: [
                <FlatButton
                    label="Close"
                    secondary={true}
                    onClick={this.props.Dialog.close}
                />,
                <FlatButton
                    label="Reset"
                    secondary={true}
                    onClick={() => {
                        data.Description.DefaultValues[vKey] = (_.has(widgetDefaultValues, vKey)) ? widgetDefaultValues[vKey] : "";
                        this.props.Dialog.close();
                    }}
                />,
                <FlatButton
                    label="Save"
                    primary={true}
                    onClick={() => {
                        try {
                            if (IsNullOrEmpty(defaultValue)) {
                                data.Description.DefaultValues[vKey] = null;
                                updateData(data);
                                // Close the dialog
                                this.props.Dialog.close();
                            } else {
                                if (port.PlegmaPort.Type == Plegma.ePortType.JsonString) {
                                    var newValue = JSON.parse(defaultValue);
                                    var valid = this.props.ValidateValues(vKey, newValue);
                                    if (valid.valid) {
                                        data.Description.DefaultValues[vKey] = newValue;
                                        updateData(data);
                                        // Close the dialog
                                        this.props.Dialog.close();
                                    } else {
                                        this.props.Dialog.error("Failed to Save: \n" + valid.errors);
                                    }
                                } else {
                                    data.Description.DefaultValues[vKey] = defaultValue;
                                    updateData(data);
                                    // Close the dialog
                                    this.props.Dialog.close();
                                }
                            }
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                            this.props.Dialog.error("Failed to Save: \n" + (ex as Error).message);
                        }
                    }}
                />
            ],
            bodyFunc: (w, h) => <MonacoEditor
                value={defaultValue}
                onChange={(value) => { defaultValue = value; }}
                width={w}
                height={h}
                language={(port.PlegmaPort.Type == Plegma.ePortType.JsonString) ? "json" : "plaintext"}
                theme="vs-dark"
                options={{ selectOnLineNumbers: true }}
            />
        };
        this.props.Dialog.show(dialogInfo);
    }
    // ---------------------------------------------------------------------------------------------------
    getPortSettingList(): SettingsElementAll<WidgetStructs.WidgetThingGenericConfiguration>[] {
        const disableRipple = !isAnimationEnabled();
        var portList: SettingsElementAll<WidgetStructs.WidgetThingGenericConfiguration>[] = [];
        _.map(this.props.Thing.Ports, (port, index) => {
            if (isWebThingKey(port.PortKey))
                return null;

            portList.push({
                label: port.PlegmaPort.Name,
                secondaryText: port.PlegmaPort.PortKey,
                type: SettingsType.ListItem,
                dataPath: port.PlegmaPort.Name + "-" + port.PlegmaPort.PortKey,
                leftIcon: (port.PlegmaPort.ioDirection == Plegma.ioPortDirection.Output) ? <OutputIcon color="#e53935" /> : <InputIcon color="#039BE5" />,
                rightIconButtonsFunc: (data, updateCB) => <IconMenu iconButtonElement={
                    <IconButton
                        touch={true}
                        tooltip="more"
                        tooltipPosition="bottom-left"
                        disableFocusRipple={disableRipple}
                        disableTouchRipple={disableRipple}>
                        <MoreVertIcon color={grey500} />
                    </IconButton>}>
                    <MenuItem leftIcon={<CopyIcon />} onClick={() => CopyToClipboard(port.PlegmaPort.PortKey)}>Copy Key</MenuItem>
                    <MenuItem
                        hidden={port.PlegmaPort.ioDirection == Plegma.ioPortDirection.Output}
                        disabled={port.PlegmaPort.ioDirection == Plegma.ioPortDirection.Output}
                        leftIcon={<DefaultEditIcon />}
                        onClick={() => this.showEditDefaultValueDialog(port, data, updateCB)}>
                        Default Value
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        rightToggle={<Toggle
                            disabled={
                                !isDefined(data.Description.PortMacros) ||
                                !_.has(data.Description.PortMacros, port.PlegmaPort.PortKey) ||
                                !_.has(data.Description.PortMacros[port.PlegmaPort.PortKey], "onDataTransform")}
                            toggled={
                                isDefined(data.Description.PortMacros) &&
                                _.has(data.Description.PortMacros, port.PlegmaPort.PortKey) &&
                                _.has(data.Description.PortMacros[port.PlegmaPort.PortKey], "onDataTransform") &&
                                data.Description.PortMacros[port.PlegmaPort.PortKey]["onDataTransform"].Enable
                            }
                            onToggle={(event, isInputChecked) => {
                                // Check if we can enable the transformation
                                if (isDefined(data.Description.PortMacros) &&
                                    _.has(data.Description.PortMacros, port.PlegmaPort.PortKey) &&
                                    _.has(data.Description.PortMacros[port.PlegmaPort.PortKey], "onDataTransform")) {
                                    data.Description.PortMacros[port.PlegmaPort.PortKey]["onDataTransform"].Enable = isInputChecked;
                                    updateCB(data);
                                }
                            }} />}>
                        Enable
                    </MenuItem>
                    <MenuItem
                        leftIcon={<DefaultEditIcon />}
                        onClick={() => this.showJSEditorDialog(port, data, updateCB)}>
                        onDataTransform
                    </MenuItem>

                </IconMenu>
            });
        })
        return portList;
    }
    // ---------------------------------------------------------------------------------------------------
    getMacrosSettingList(data: WidgetStructs.WidgetThingGenericConfiguration): SettingsElementAll<WidgetStructs.WidgetThingGenericConfiguration>[] {
        var macrosList: SettingsElementAll<WidgetStructs.WidgetThingGenericConfiguration>[] = [];
        _.map(data.Description.JSMacros, (macro, index) => {
            macrosList.push({
                label: macro.Name,
                secondaryText: macro.Description,
                type: SettingsType.ListItem,
                dataPath: macro.Name + "-" + index,
                leftIcon: <MacroIcon color="#039BE5" />,
                rightIconButtonsFunc: (data, updateCB) => <IconMenu iconButtonElement={<IconButton
                    touch={true}
                    tooltip="more"
                    tooltipPosition="bottom-left"
                    disableFocusRipple={!isAnimationEnabled()}
                    disableTouchRipple={!isAnimationEnabled()}>
                    <MoreVertIcon color={grey500} />
                </IconButton>}>
                    <MenuItem
                        rightToggle={<Toggle
                            toggled={data.Description.JSMacros[index].Enable}
                            onToggle={(event, isInputChecked) => {
                                data.Description.JSMacros[index].Enable = isInputChecked;
                                updateCB(data);
                            }} />}>
                        Enable
                    </MenuItem>
                    <MenuItem
                        leftIcon={<DefaultEditIcon />}
                        onClick={() => {
                            let defaultValue = macro.Code;
                            let dialogInfo: DialogConf = {
                                contentStaticSize: 0.95,
                                bodyFunc: (w, h) => {
                                    let theme = BoardTheming.GetActiveTheme();
                                    return <JSEditor
                                        width={w}
                                        height={h}
                                        toolbarHeight={theme.dialog.height}
                                        Title={"Macro: " + macro.Name}
                                        JSCode={defaultValue}
                                        onCloseRequest={() => this.props.Dialog.close()}
                                        onSaveRequest={(jsCode) => {
                                            data.Description.JSMacros[index].Code = jsCode;
                                            updateCB(data);
                                        }}
                                    />
                                }
                            };
                            this.props.Dialog.show(dialogInfo);
                        }}>
                        Edit
                    </MenuItem>
                </IconMenu>
            });
        });
        return macrosList;
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        const disableRipple = !isAnimationEnabled();
        const config = this.state.GenericConfigurationsInit;

        let settings: SettingsGroup<WidgetStructs.WidgetThingGenericConfiguration>[] = [
            {
                name: "Thing",
                startOpen: false,
                elements: [
                    {
                        label: "Name",
                        type: SettingsType.String,
                        dataPath: "Description.Title"
                    },
                    {
                        label: "ThingKey",
                        type: SettingsType.ListItem,
                        dataPath: "ThingKey",
                        secondaryText: this.props.NodeKey + '-' + this.props.ThingKey,
                        rightIconButtonsFunc: () => <IconButton
                            touch={true}
                            onClick={() => CopyToClipboard(this.props.NodeKey + '-' + this.props.ThingKey)}
                            disableFocusRipple={disableRipple}
                            disableTouchRipple={disableRipple}>
                            <CopyIcon color={grey500} />
                        </IconButton>
                    }
                ]
            },
            {
                name: "Ports",
                startOpen: false,
                elementsFunc: () => this.getPortSettingList()
            },
            {
                name: "Macros",
                startOpen: false,
                elementsFunc: (data) => this.getMacrosSettingList(data)
            },
            {
                name: "Toolbar",
                startOpen: false,
                elements: [
                    {
                        label: "Title",
                        name: "Title",
                        type: SettingsType.Group,
                        elements: [
                            {
                                label: "Text",
                                type: SettingsType.String,
                                dataPath: "Description.WidgetTitle"
                            },
                            {
                                label: "Tooltip",
                                type: SettingsType.String,
                                dataPath: "Description.TitleStyle.Tooltip"
                            },
                            {
                                type: SettingsType.Icon,
                                label: "Icon",
                                dataPath: "Description.TitleStyle.Icon"
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Alignment",
                                dataPath: "Description.TitleStyle.Alignment",
                                items: [
                                    { value: TitleAlign.Left, label: "Left" },
                                    { value: TitleAlign.Center, label: "Center" },
                                    { value: TitleAlign.Right, label: "Right" }
                                ],
                                isHidden: data => IsNullOrEmpty(data.Description.WidgetTitle)
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: "Color",
                                dataPath: "Description.TitleStyle.FontColor"
                            },
                            {
                                type: SettingsType.Boolean,
                                label: "Auto Font Size",
                                dataPath: "Description.TitleStyle.FontAutoSize"
                            },
                            {
                                type: SettingsType.Slider,
                                label: "Font Size",
                                dataPath: "Description.TitleStyle.FontSize",
                                isHidden: data => data.Description.TitleStyle.FontAutoSize,
                                minValue: 10,
                                maxValue: 24,
                                stepValue: 1,
                                valuePosfix: "px"
                            }
                        ]
                    },
                    {
                        label: "Toolbar Icons",
                        type: SettingsType.ItemsMgmtDialog,
                        dataPath: "Description.ToolbarIcons",
                        icon: <ToolbarIcon color={"gray"} />,
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        dialogStaticSize: 0.95,
                        createNewItem: () => {
                            return {
                                icon: "",
                                type: "icon",
                                tooltip: "Icon tooltip",
                                onIconClickDropdown: false,
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                nestedItems: []
                            } as ToolbarIconInfo;
                        },
                    },
                    {
                        type: SettingsType.Group,
                        label: 'Track Updates',
                        name: 'Track Updates',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Enable',
                                dataPath: 'Description.TrackUpdates.ShowLastUpdateTs'
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'Date Format',
                                dataPath: 'Description.TrackUpdates.DateFormat',
                                items: [
                                    { value: DateFormat.Short, label: 'Short' },
                                    { value: DateFormat.Long, label: 'Long' },
                                    { value: DateFormat.ISO, label: 'ISO' },
                                    { value: DateFormat.ISOMS, label: 'ISO ms' }
                                ],
                                isHidden: data => !data.Description.TrackUpdates.ShowLastUpdateTs
                            },
                            {
                                type: SettingsType.DropDown,
                                label: 'View Format',
                                dataPath: 'Description.TrackUpdates.ViewFormat',
                                items: [
                                    { value: ITsViewFormat.Tooltip, label: 'Icon tooltip' },
                                    { value: ITsViewFormat.Hover, label: 'Hover tooltip' },
                                    { value: ITsViewFormat.Text, label: 'Text' }
                                ],
                                isHidden: data => !data.Description.TrackUpdates.ShowLastUpdateTs
                            }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Zoom",
                        dataPath: "Description.ShowExtraMenu"
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "Description.TitleStyle.TitleHeight",
                        minValue: 25,
                        maxValue: 40,
                        stepValue: 1,
                        valuePosfix: "px"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: "Background Color",
                        dataPath: "Description.TitleStyle.BackgroundColor"
                    }
                ]
            },
            {
                name: "Layout/Theme",
                startOpen: false,
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Defy grid layout",
                        dataPath: "Description.DefiesGrid"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Transparent",
                        dataPath: "Description.WidgetNoBackground"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: "Start with loading",
                        dataPath: "Description.StartWithLoading"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        isNullable: true,
                        label: "Background Color",
                        dataPath: "Description.WidgetBackgroundColor",
                        isHidden: data => data.Description.WidgetNoBackground
                    },
                    {
                        type: SettingsType.Group,
                        label: 'Padding',
                        name: 'Padding',
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Fixed',
                                dataPath: 'Description.WidgetPadding.Fixed'
                            },
                            {
                                type: SettingsType.Boolean,
                                label: 'Evenly among sides',
                                dataPath: 'Description.WidgetPadding.Common'
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Percent',
                                dataPath: 'Description.WidgetPadding.All',
                                minValue: 0,
                                maxValue: 50,
                                stepValue: 1,
                                valuePosfix: '%',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return pad.Fixed || !pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Top percent',
                                dataPath: 'Description.WidgetPadding.Top',
                                minValue: 0,
                                maxValue: 50,
                                stepValue: 1,
                                valuePosfix: '%',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Right percent',
                                dataPath: 'Description.WidgetPadding.Right',
                                minValue: 0,
                                maxValue: 50,
                                stepValue: 1,
                                valuePosfix: '%',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Bottom percent',
                                dataPath: 'Description.WidgetPadding.Bottom',
                                minValue: 0,
                                maxValue: 50,
                                stepValue: 1,
                                valuePosfix: '%',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Left percent',
                                dataPath: 'Description.WidgetPadding.Left',
                                minValue: 0,
                                maxValue: 50,
                                stepValue: 1,
                                valuePosfix: '%',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Length',
                                dataPath: 'Description.WidgetPadding.All',
                                minValue: 0,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return !pad.Fixed || !pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Top length',
                                dataPath: 'Description.WidgetPadding.Top',
                                minValue: 0,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return !pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Right length',
                                dataPath: 'Description.WidgetPadding.Right',
                                minValue: 0,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return !pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Bottom length',
                                dataPath: 'Description.WidgetPadding.Bottom',
                                minValue: 0,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return !pad.Fixed || pad.Common;
                                }
                            },
                            {
                                type: SettingsType.Slider,
                                label: 'Left length',
                                dataPath: 'Description.WidgetPadding.Left',
                                minValue: 0,
                                maxValue: 200,
                                stepValue: 1,
                                valuePosfix: 'px',
                                isHidden: data => {
                                    const pad = data.Description.WidgetPadding;
                                    return !pad.Fixed || pad.Common;
                                }
                            }
                        ]
                    },
                    {
                        type: SettingsType.Group,
                        label: 'Outline',
                        name: 'Outline',
                        elements: [
                            {
                                type: SettingsType.Slider,
                                isNullable: true,
                                label: 'Thickness',
                                dataPath: 'Description.Outline.Thickness',
                                minValue: 0,
                                maxValue: 8,
                                stepValue: 1,
                                valuePosfix: 'px'
                            },
                            {
                                type: SettingsType.ColorPicker,
                                label: 'Color',
                                dataPath: 'Description.Outline.Color'
                            }
                        ]
                    }
                ]
            }];

        return (
            <List style={{ marginTop: -8, marginBottom: -8 }}>
                <ListItem
                    key="Generic Configuration"
                    primaryText="Generic Configuration"
                    primaryTogglesNestedList={true}
                    style={{ backgroundColor: "#ff9e01" }}
                    nestedItems={[
                        <div
                            key='div-settings-list'
                            style={{ marginTop: -8, marginBottom: -8 }}>
                            <SettingsList
                                numberOfGridColumn={1}
                                AutoSave={true}
                                SettingsMap={settings}
                                Data={config}
                                onSaveData={(data: WidgetStructs.WidgetThingGenericConfiguration) => {
                                    this.props.onSaveSettings(data)
                                }}
                            />
                        </div>
                    ]} />
                <ListItem
                    key="Widget Configuration"
                    primaryText="Widget Configuration"
                    initiallyOpen={true}
                    primaryTogglesNestedList={true}
                    style={{ backgroundColor: "#ff9e01" }}
                    nestedItems={[
                        <div
                            key='div-widget-settings'
                            style={{ marginTop: -8, marginBottom: -8 }}>
                            {this.props.RenderWidgetSettings}
                        </div>
                    ]} />
                <Divider />
                <ListItem
                    key="Documentation"
                    primaryText="Documentation"
                    initiallyOpen={true}
                    primaryTogglesNestedList={true}
                    style={{ backgroundColor: "#ff9e01" }}
                    rightIconButton={
                        <IconButton
                            disableFocusRipple={disableRipple}
                            disableTouchRipple={disableRipple}
                            onClick={() => {
                                var dialogInfo: DialogConf = {
                                    contentStaticSize: { h: 0.95, w: 0.95 },
                                    bodyFunc: this.props.DescriptionJSXFunc,
                                    handleClose: this.props.Dialog.close
                                };
                                this.props.Dialog.show(dialogInfo);
                            }}>
                            <OpenInDialogIcon />
                        </IconButton>
                    }
                />
                < Divider />
            </List >);
    }
}
// ------------------------------------------------------------------------------------------------
export var LazyWidget = (props: React.Props<any>) => (
    <React.Suspense fallback={<LazyLoadingAnimation />}>
        {React.Children.map(props.children,
            (child: React.ReactElement<any>, i) =>
                <child.type
                    {...props}
                    {...child.props}
                />
        )}
    </React.Suspense>
);
// ------------------------------------------------------------------------------------------------
export interface IWidgetThingsBaseComponentProps extends IWidgetThingsProps<any> {
    StartWithLoading: boolean;
    SupportEditing: boolean;
    Padding: IWidgetPadding;
}
// ------------------------------------------------------------------------------------------------
export interface IWidgetThingsBaseComponentState {
    hasError: boolean;

    PortsStates: { [key: string]: PortData };
    LastThingStatus: StateStatus;
    Loading: boolean;
    Version: number;
}
// ------------------------------------------------------------------------------------------------
export class IWidgetThingsBaseComponent extends React.Component<IWidgetThingsBaseComponentProps, IWidgetThingsBaseComponentState> {
    static whyDidYouRender = true;
    // ------------------------------------------------------------------------------------------------
    constructor(props: IWidgetThingsBaseComponentProps) {
        super(props);
        let state = {
            hasError: false,
            PortsStates: {},
            LastThingStatus: null,
            Loading: false,
            Version: -1
        };

        // Read the current status of the widget
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager)) {
            let stateThing = stateManager.GetState(this.props.ThingKey);
            if (isDefined(stateThing)) {
                state.PortsStates = stateThing.PortStates;
                state.LastThingStatus = stateThing.LastStateStatus;
                state.Loading = stateThing.Loading;
                state.Version = stateThing.Version;
            }
        }

        this.state = state;
    }
    // ------------------------------------------------------------------------------------------------
    componentDidMount(): void {
        let newState = _.clone(this.state) as IWidgetThingsBaseComponentState;

        // Register in state manager
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager)) {
            stateManager.RegisterCB(this.props.ThingKey, this.stateUpdate);
            let stateThing = stateManager.GetState(this.props.ThingKey);
            if (isDefined(stateThing)) {
                newState.PortsStates = stateThing.PortStates;
                newState.LastThingStatus = stateThing.LastStateStatus;
                newState.Loading = stateThing.Loading;
                newState.Version = stateThing.Version;
            }
        }

        if (!_.isEqual(newState, this.state))
            this.setState(newState);
    }
    // ------------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let stateManager = StateManager.GetInstanceKey(this.props.NodeKey);
        if (isDefined(stateManager))
            stateManager.UnRegisterCB(this.props.ThingKey, this.stateUpdate);
    }
    // ------------------------------------------------------------------------------------------------
    stateUpdate = (state: ReduxThingState, forceUpdate: boolean) => {
        if (this.state.Version != state.Version || forceUpdate)
            this.setState({
                PortsStates: state.PortStates,
                LastThingStatus: state.LastStateStatus,
                Loading: state.Loading,
                Version: state.Version
            });
    }
    // ------------------------------------------------------------------------------------------------
    componentDidCatch(error, info) {
        DebugEx.TraceException(error, info);
        // Display fallback UI
        this.setState({ hasError: true });
    }
    // ------------------------------------------------------------------------------------------------
    private transformPadding(wd: number, ht: number, pad: IWidgetPadding): IWidgetPadding {
        if (pad.Common) {
            const val = pad.All;
            if (pad.Fixed) {
                // Evenly fixed padding among sides.
                pad.Top = val;
                pad.Right = val;
                pad.Bottom = val;
                pad.Left = val;
            }
            else {
                // Vertical padding computation.
                const ver = Math.round(ht * val / 100);
                pad.Top = ver;
                pad.Bottom = ver;

                // Horizontal padding computation.
                const hor = Math.round(wd * val / 100);
                pad.Left = hor;
                pad.Right = hor;
            }
        } else {
            if (!pad.Fixed) {
                // Separate relative padding to each side.
                pad.Top = Math.round(ht * pad.Top / 100);
                pad.Right = Math.round(wd * pad.Right / 100);
                pad.Bottom = Math.round(ht * pad.Bottom / 100);
                pad.Left = Math.round(wd * pad.Left / 100);
            }
        }
        return pad;
    }
    // ------------------------------------------------------------------------------------------------
    render() {
        const wd = this.props.width;
        const ht = this.props.height;
        let {
            WidgetData,
            ...props
        } = this.props;

        if (this.state.hasError)
            return <div
                style={{
                    width: wd,
                    height: ht,
                    textAlign: 'center',
                    fontSize: 'medium',
                    fontWeight: 700,
                    backgroundColor: "#e5e3df",
                    color: "#757575",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex"
                }}>
                <div>
                    <ErrorIcon style={{
                        width: 32,
                        height: 32,
                        color: "#757575"
                    }} />
                    {this.props.height < 40
                        ? null
                        : <p>{"Oops! Something went wrong."}</p>}
                    <FlatButton
                        labelStyle={{ fontSize: 12 }}
                        label="Reset widget"
                        onClick={() => this.setState({ hasError: false })} />
                </div>
            </div>;

        const status = this.state.LastThingStatus;
        if (this.props.StartWithLoading === true &&
            status === StateStatus.NoData)
            return <LazyLoadingAnimation
                style={{
                    height: ht,
                    width: wd
                }}
            />;

        let errorHighlight = status == StateStatus.DataNotValid || status == StateStatus.JsonError;
        const color = status == StateStatus.JsonError ? "#ff0600c9" : "#ff9800cc";

        // Don't highlight if we don't support editing.
        if (!this.props.SupportEditing)
            errorHighlight = false;

        // Get padding and new size.
        const pad = this.transformPadding(wd, ht, _.cloneDeep(this.props.Padding));
        const newWd = wd - (pad.Left + pad.Right);
        const newHt = ht - (pad.Top + pad.Bottom);

        const locInst = Localization.GetInstance(this.props.NodeKey);
        let locMap = {};
        if (locInst != null)
            locMap = locInst.GetTextMap(this.props.ThingKey);

        // Merge widget data
        if (this.state.PortsStates[WebThingKeys.WidgetSettings]?.LastStateStatus == StateStatus.Valid) {
            let settings = this.state.PortsStates[WebThingKeys.WidgetSettings].State;
            WidgetData = _.merge({}, WidgetData, settings);
        }

        return <div style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            width: wd,
            height: ht,
            paddingLeft: pad.Left,
            paddingRight: pad.Right,
            paddingTop: pad.Top,
            paddingBottom: pad.Bottom
        }}>
            {/* Error highlighting. */}
            {errorHighlight
                ? <div style={{
                    width: newWd,
                    height: newHt,
                    position: "absolute",
                    zIndex: 2500,
                    pointerEvents: "none",
                    boxShadow: "inset 0px 0px 20px 5px" + color
                }} />
                : null}

            {/* Loading indicator. */}
            {this.state.Loading
                ? <LazyLoadingAnimation style={{
                    width: newWd,
                    height: newHt,
                    position: "absolute",
                    zIndex: 2500
                }} />
                : null}

            {/* Widget content. */}
            {React.Children.map(
                this.props.children,
                (child: React.ReactElement<IWidgetThingsProps<any>>) =>
                    <child.type
                        {...props}
                        {...child.props}
                        WidgetData={WidgetData}
                        width={newWd}
                        height={newHt}
                        PortsStates={this.state.PortsStates}
                        PortVersion={this.state.Version}
                        LastThingStatus={status}
                        Loading={this.state.Loading}
                        LocalizationMap={locMap}
                    />
            )}
        </div>;
    }
    // ------------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export const enum WidgetLocalizationKeys {
    Title = "WidgetDescriptor_Title"
}
// ------------------------------------------------------------------------------------------------
export function DefaultMessageOnNoData(width: number, height: number): React.ReactElement<any> {
    return <div
        key='no-data-div'
        style={{
            width: width,
            height: height,
            textAlign: 'center',
            fontSize: 'large',
            fontWeight: 700,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
        }}>
        <NoDataIcon />
        <div>
            {"No data to present"}
        </div>
    </div>;
}
// ------------------------------------------------------------------------------------------------
export const defaultWidgetContainerPadding: number = 0;
// ------------------------------------------------------------------------------------------------
export const IconURIPath = "https://dashboard.yodiwo.com/Content/Icons";
